import { useEffect, useRef } from "react";
import { NetworkEngine } from "@/lib/network";
import { useActiveSection } from "@/hooks/usePortfolio";

const SECTION_MODE = {
  hero: "default",
  about: "default",
  stack: "embeddings",
  work: "packets",
  experience: "clusters",
  contact: "default",
};

// Living network background — alive for the whole experience, adapts per section.
export function NetworkCanvas() {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const active = useActiveSection(Object.keys(SECTION_MODE));

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const canvas = canvasRef.current;
    const isMobile = window.innerWidth < 640;
    const engine = new NetworkEngine(canvas, { densityScale: isMobile ? 0.8 : 1 });
    engineRef.current = engine;
    engine.setSpawnRatio(isMobile ? 0.85 : 1);

    const onResize = () => engine.resize();
    let lastY = window.scrollY;
    let vel = 0;
    const onScroll = () => {
      vel = window.scrollY - lastY;
      lastY = window.scrollY;
      engine.setScroll(vel, window.scrollY);
    };
    const onMove = (e) => engine.setMouse(e.clientX, e.clientY, true);
    const onTouchMove = (e) => {
      const touch = e.touches?.[0];
      if (touch) engine.setMouse(touch.clientX, touch.clientY, true);
    };
    const onTouchEnd = () => engine.setMouse(-9999, -9999, false);
    const onLeave = () => engine.setMouse(-9999, -9999, false);

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchstart", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("mouseout", onLeave);

    if (reduce) {
      engine.setSpawnRatio(0.7);
      engine.step(1); // single static frame
    } else {
      engine.start();
    }

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchstart", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("mouseout", onLeave);
      engine.destroy();
    };
  }, []);

  useEffect(() => {
    if (engineRef.current) engineRef.current.setMode(SECTION_MODE[active] || "default");
  }, [active]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0 h-full w-full"
      style={{ opacity: window.innerWidth < 640 ? 0.7 : 0.55 }}
    />
  );
}
